import type { ReducceEvent, EventsResponse } from "@/features/boutique/types/event.type";
import type {
  RawTicketingAggregations,
  TicketingEventsResponse,
  TicketingFilters,
} from "@/features/boutique/types/ticketing.type";
import { transformAggregations } from "@/features/boutique/utils/ticketing-aggregations.util";
import { mapEventToRef } from "@/features/boutique/utils/ticketing-mappers.util";
import { fetcher } from "@/shared/lib/fetcher.client";

export const TICKETING_CATALOGUE_ID = 5;

type TicketingSearchApiResponse = {
  events: ReducceEvent[]
  aggregations: RawTicketingAggregations
  metadata: {
    total_count: number
    current_page: number
    total_pages: number
  }
};

const buildSearchParams = (filters: TicketingFilters): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    page: filters.page || 1,
    per_page: filters.perPage || 20,
  };

  const normalizedSearch = filters.search?.trim();
  if (normalizedSearch) {
    params.q = normalizedSearch;
  }

  if (filters.genreIds?.length) {
    params["genre_id"] = filters.genreIds;
  }

  if (filters.sousgenreIds?.length) {
    params["sousgenre_id"] = filters.sousgenreIds;
  }

  if (filters.regions?.length) {
    params["region"] = filters.regions;
  }

  if (filters.cities?.length) {
    params["city"] = filters.cities;
  }

  if (filters.priceRange) {
    params.price_range = filters.priceRange;
  }

  if (filters.dateRange) {
    params.date_range = filters.dateRange;
  }

  if (filters.sort) {
    params.sort = filters.sort;
  }

  return params;
};

export const ticketingService = {
  searchEvents: async (filters: TicketingFilters): Promise<TicketingEventsResponse> => {
    const params = buildSearchParams(filters);

    const response = await fetcher.get<TicketingSearchApiResponse>(
      `/rce-catalogues/${TICKETING_CATALOGUE_ID}/events/search`,
      { params },
    );

    return {
      events: response.data.events.map(mapEventToRef),
      aggregations: transformAggregations(response.data.aggregations),
      metadata: response.data.metadata,
    };
  },

  getEventById: async (eventId: string): Promise<ReducceEvent | null> => {
    const response = await fetcher.get<EventsResponse>(
      `/rce-catalogues/${TICKETING_CATALOGUE_ID}/events`,
      { params: { "q[id_eq]": eventId } },
    );

    return response.data.events[0] ?? null;
  },
};
